import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import { Search } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { faqService } from '../api';
import { ThemedText, Collapsible } from '../components';
import { getCategoryIcon } from '../constants/faqCategories';

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p','br','strong','b','em','i','u','s','a','img','ul','ol','li','blockquote','code','pre','h1','h2','h3','h4','h5','h6','span','div'],
  ALLOWED_ATTR: ['href','src','alt','style','target','rel','class','width','height'],
};

const sanitizeHtml = (html) => DOMPurify.sanitize(html || '', SANITIZE_CONFIG);

const stripHtmlTags = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return (tmp.textContent || tmp.innerText || '').trim();
};

const HelpPageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
`;

const HelpHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  position: relative;
  width: 320px;
  max-width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  padding-left: 36px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.sizes.default};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.text};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }

  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.muted};
  pointer-events: none;
`;

const HelpLayout = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xl};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div`
  width: 280px;
  flex-shrink: 0;
  position: sticky;
  top: ${props => props.theme.spacing.lg};
  align-self: flex-start;

  @media (max-width: 768px) {
    width: 100%;
    position: static;
    display: flex;
    gap: ${props => props.theme.spacing.sm};
    overflow-x: auto;
    padding-bottom: ${props => props.theme.spacing.sm};
  }
`;

const SidebarItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.$active ? props.theme.colors.tint + '15' : 'transparent'};
  color: ${props => props.$active ? props.theme.colors.tint : props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: ${props => props.$active ? '600' : '400'};
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$active ? props.theme.colors.tint + '15' : props.theme.colors.backgroundSecondary};
  }

  @media (max-width: 768px) {
    width: auto;
    flex-shrink: 0;
  }
`;

const SidebarCount = styled.span`
  margin-left: auto;
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  background: ${props => props.theme.colors.backgroundSecondary};
  padding: 2px 8px;
  border-radius: 10px;

  @media (max-width: 768px) {
    margin-left: ${props => props.theme.spacing.xs};
  }
`;

const ContentArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const CategorySection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.typography.sizes.large};
  font-weight: 600;
  color: ${props => props.theme.colors.tint};
  margin-bottom: ${props => props.theme.spacing.md};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const CategoryCount = styled.span`
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 400;
  color: ${props => props.theme.colors.muted};
`;

const FaqCard = styled.div`
  margin-bottom: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: ${props => props.theme.shadows.sm};
  }
`;

const RichTextContent = styled.div`
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  font-size: ${props => props.theme.typography.sizes.default};

  h1, h2, h3 { color: ${props => props.theme.colors.text}; margin: ${props => props.theme.spacing.md} 0 ${props => props.theme.spacing.sm}; }
  p { margin: ${props => props.theme.spacing.sm} 0; }
  ul, ol { padding-left: ${props => props.theme.spacing.xl}; }
  code { background: ${props => props.theme.colors.background}; padding: 2px 6px; border-radius: 4px; font-size: 0.9em; }
  pre { background: ${props => props.theme.colors.background}; padding: ${props => props.theme.spacing.md}; border-radius: ${props => props.theme.borderRadius.md}; overflow-x: auto; }
  a { color: ${props => props.theme.colors.tint}; }
  img { max-width: 100%; border-radius: ${props => props.theme.borderRadius.md}; }
  blockquote { border-left: 3px solid ${props => props.theme.colors.border}; padding-left: ${props => props.theme.spacing.md}; color: ${props => props.theme.colors.muted}; margin: ${props => props.theme.spacing.sm} 0; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.muted};
`;

export const HelpPage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const data = await faqService.getPublicFaqs(user?.organizationId);
      setFaqs(data);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  // Filter by search
  const filteredFaqs = faqs.filter(faq => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    const plainAnswer = stripHtmlTags(faq.answer);
    return faq.question.toLowerCase().includes(term) ||
      plainAnswer.toLowerCase().includes(term) ||
      (faq.category && faq.category.toLowerCase().includes(term));
  });

  // Group FAQs
  const groupedFaqs = faqService.groupByCategory(filteredFaqs);
  const categoryIcons = groupedFaqs._categoryIcons || {};
  const categories = Object.keys(groupedFaqs).filter(k => k !== '_categoryIcons');

  // Filter by selected category
  const visibleCategories = selectedCategory === 'All'
    ? categories
    : categories.filter(c => c === selectedCategory);

  const totalCount = filteredFaqs.length;

  return (
    <Layout currentPath="/help">
      <HelpPageContainer theme={theme}>
        <HelpHeader theme={theme}>
          <ThemedText type="title">Help Center</ThemedText>
          <SearchBar>
            <SearchIconWrapper theme={theme}>
              <Search size={16} />
            </SearchIconWrapper>
            <SearchInput
              theme={theme}
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </SearchBar>
        </HelpHeader>

        {loading ? (
          <LoadingState theme={theme}>
            <ThemedText>Loading FAQs...</ThemedText>
          </LoadingState>
        ) : faqs.length === 0 ? (
          <EmptyState theme={theme}>
            <ThemedText>No FAQs available yet.</ThemedText>
          </EmptyState>
        ) : (
          <HelpLayout theme={theme}>
            <Sidebar theme={theme}>
              <SidebarItem
                theme={theme}
                $active={selectedCategory === 'All'}
                onClick={() => setSelectedCategory('All')}
              >
                All
                <SidebarCount theme={theme}>{totalCount}</SidebarCount>
              </SidebarItem>
              {categories.map(category => {
                const CatIcon = getCategoryIcon(category, categoryIcons[category]);
                return (
                  <SidebarItem
                    key={category}
                    theme={theme}
                    $active={selectedCategory === category}
                    onClick={() => setSelectedCategory(category)}
                  >
                    <CatIcon size={16} />
                    {category}
                    <SidebarCount theme={theme}>{groupedFaqs[category].length}</SidebarCount>
                  </SidebarItem>
                );
              })}
            </Sidebar>

            <ContentArea>
              {visibleCategories.length === 0 ? (
                <EmptyState theme={theme}>
                  <ThemedText>
                    {searchTerm ? 'No FAQs found matching your search.' : 'No FAQs in this category.'}
                  </ThemedText>
                </EmptyState>
              ) : (
                visibleCategories.map(category => {
                  const CatIcon = getCategoryIcon(category, categoryIcons[category]);
                  const categoryFaqs = groupedFaqs[category];
                  return (
                    <CategorySection key={category} theme={theme}>
                      <CategoryHeader theme={theme}>
                        <CatIcon size={20} />
                        {category}
                        <CategoryCount theme={theme}>({categoryFaqs.length})</CategoryCount>
                      </CategoryHeader>
                      {categoryFaqs.map(faq => (
                        <FaqCard key={faq.id} theme={theme}>
                          <Collapsible title={faq.question}>
                            <RichTextContent
                              theme={theme}
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(faq.answer) }}
                            />
                          </Collapsible>
                        </FaqCard>
                      ))}
                    </CategorySection>
                  );
                })
              )}
            </ContentArea>
          </HelpLayout>
        )}
      </HelpPageContainer>
    </Layout>
  );
};
